import * as React from 'react';
import { Badge } from './Badge';
import { callStatusLabel } from './call-status';
import { DataValue } from './DataValue';
import { Inline } from './Layout';
import {
  PriorityTag,
  RecordRow,
  RecordRowTag,
  WEIGHT_WORD,
  WeightClassBars,
  type CallPriority,
  type RecordRowProps,
  type WeightClass,
} from './RecordRow';
import { StatusChip } from './StatusChip';
import { toneForStatus, type StatusTone } from './status-tone';
import { Text } from './Text';

/**
 * Each kind of record's card, so a list screen says what the record is and
 * the card says how it reads. The library's Resource cards section draws
 * these same components. Each screen wrote its card out, and the phone's call
 * card came to the dashboard with the phone's sizes.
 *
 * `platform` is required for that reason: the dashboard's pills are the
 * smaller Badge and the phone's the StatusChip, and neither is a default.
 * The values are the screen's, already in words.
 */
export type CardPlatform = 'dashboard' | 'phone';

interface ResourceCardProps
  extends Pick<RecordRowProps, 'selected' | 'onClick'> {
  platform: CardPlatform;
}

/** A card's pill, at its platform's size. */
function CardPill({
  platform,
  label,
  tone = 'neutral',
  title,
}: {
  platform: CardPlatform;
  label: string;
  tone?: StatusTone;
  title?: string;
}) {
  return platform === 'phone' ? (
    <StatusChip label={label} tone={tone} title={title} />
  ) : (
    <Badge tone={tone} title={title}>
      {label}
    </Badge>
  );
}

const plateOf = (plate: string | null | undefined) =>
  plate ? `Plate ${plate}` : undefined;

export interface CallCardProps extends ResourceCardProps {
  /** Where the call is: the first line of the pickup address. */
  address: string;
  /** The town it is in, under the address: "Huntington Beach". */
  locality?: React.ReactNode;
  /** A status string from the API, coloured through statusTone. */
  status: string;
  /** What the chip says, when not callStatusLabel's word for it. */
  statusLabel?: string;
  priority?: CallPriority;
  /** The call's number, muted beside the priority tag: "096" reads "#096". */
  number?: string;
  weightClass?: WeightClass;
}

/**
 * A call: the priority tag and number, the street and town, the status and
 * the weight class, as the Figma's list draws it (644:12248). The time it was
 * booked used to stand in for a missing weight class; the Figma has none, and
 * the number is what a driver and the office say to each other.
 *
 *   <CallCard platform="phone" address="La Jolla Cir"
 *     locality="Huntington Beach" status="DISPATCHED" priority="HIGH"
 *     number="096" weightClass="heavy" onClick={open} />
 *
 * Every row has a priority tag, as the Figma shows. Normal and low are a
 * faint grey so a list of them reads as quiet; high and urgent take colour,
 * which is what lets the eye find them.
 *
 * That Figma is the phone's, ruled off with no column around it. The
 * dashboard's has the same parts at the dashboard's sizes, in a list column.
 */
export function CallCard({
  platform,
  address,
  locality,
  status,
  statusLabel,
  priority,
  number,
  weightClass,
  ...props
}: CallCardProps) {
  const dashboard = platform === 'dashboard';
  const size = dashboard ? 'dashboard' : 'phone';
  return (
    <RecordRow
      density={dashboard ? 'card' : undefined}
      title={address}
      meta={locality}
      badge={
        priority || number ? (
          <Inline as="span" gap={2} align="center">
            {priority ? <PriorityTag priority={priority} size={size} /> : null}
            {number ? (
              <Text as="span" variant="body" tone="muted" numeric>
                #{number}
              </Text>
            ) : null}
          </Inline>
        ) : undefined
      }
      status={
        <CardPill
          platform={platform}
          label={statusLabel ?? callStatusLabel(status)}
          tone={toneForStatus(status)}
        />
      }
      figure={
        weightClass ? (
          <Inline as="span" gap={2} align="end">
            {WEIGHT_WORD[weightClass]}
            <WeightClassBars weightClass={weightClass} size={size} />
          </Inline>
        ) : undefined
      }
      {...props}
    />
  );
}

export interface ImpoundCardProps extends ResourceCardProps {
  number: string;
  /** The vehicle's colour as it was typed: a dot, and the word. */
  color?: string | null;
  vehicle: string;
  lot?: string | null;
  /** "12 days". Left out for someone who only drives, who is not sent it. */
  heldFor?: string;
  plate?: string | null;
}

/** An impound, from Figma 1027:25114. */
export function ImpoundCard({
  platform,
  number,
  color,
  vehicle,
  lot,
  heldFor,
  plate,
  ...props
}: ImpoundCardProps) {
  return (
    <RecordRow
      density="card"
      badge={<RecordRowTag color={color}>{number}</RecordRowTag>}
      title={vehicle}
      meta={lot}
      status={
        heldFor ? <CardPill platform={platform} label={heldFor} /> : undefined
      }
      figure={plateOf(plate)}
      {...props}
    />
  );
}

export interface InvoiceCardProps extends ResourceCardProps {
  number: string;
  client: string;
  vehicle?: string;
  /** The status in words, where the list does not already say it. */
  status?: string;
  statusTone?: StatusTone;
  /** Already formatted: "$245.00". */
  total: string;
}

/** An invoice. */
export function InvoiceCard({
  platform,
  number,
  client,
  vehicle,
  status,
  statusTone,
  total,
  ...props
}: InvoiceCardProps) {
  return (
    <RecordRow
      density="card"
      badge={<RecordRowTag>{number}</RecordRowTag>}
      title={client}
      meta={vehicle}
      status={
        status ? (
          <CardPill platform={platform} label={status} tone={statusTone} />
        ) : undefined
      }
      figure={total}
      {...props}
    />
  );
}

export interface InspectionCardProps extends ResourceCardProps {
  /** The day alone: "Yesterday". The record beside it has the time. */
  day: string;
  /** What it was run on: "Truck 7". */
  subject: string;
  /** Who filed it, where they are not all the viewer's own. */
  filedBy?: string;
  result: string;
  resultTone?: StatusTone;
  /** What it found: the pill counts them, in place of the result. */
  issues?: number;
  checklist?: string;
}

/** An inspection report. */
export function InspectionCard({
  platform,
  day,
  subject,
  filedBy,
  result,
  resultTone,
  issues = 0,
  checklist,
  ...props
}: InspectionCardProps) {
  return (
    <RecordRow
      density="card"
      badge={<RecordRowTag>{day}</RecordRowTag>}
      title={subject}
      meta={filedBy}
      status={
        <CardPill
          platform={platform}
          label={
            issues > 0
              ? `${issues} ${issues === 1 ? 'issue' : 'issues'}`
              : result
          }
          tone={resultTone}
        />
      }
      figure={checklist}
      {...props}
    />
  );
}

export interface DriverCardProps extends ResourceCardProps {
  employeeNumber?: string | null;
  name: string;
  truckNumber?: string | null;
  /** A status string from the API, coloured through statusTone. */
  status?: string;
  statusLabel: string;
  phone?: string | null;
}

/** A driver. */
export function DriverCard({
  platform,
  employeeNumber,
  name,
  truckNumber,
  status,
  statusLabel,
  phone,
  ...props
}: DriverCardProps) {
  return (
    <RecordRow
      density="card"
      badge={
        employeeNumber ? (
          <RecordRowTag>{employeeNumber}</RecordRowTag>
        ) : undefined
      }
      title={name}
      meta={truckNumber ? `Truck ${truckNumber}` : undefined}
      status={
        <CardPill
          platform={platform}
          label={statusLabel}
          tone={toneForStatus(status)}
        />
      }
      figure={phone ? <DataValue value={phone} format="phone" /> : undefined}
      {...props}
    />
  );
}

export interface TruckCardProps extends ResourceCardProps {
  /** The truck's number, or its plate where it has none. */
  name: string;
  /** Its make and model, in as few words as a row can spare. */
  model?: string;
  /** What it is: "Flat bed". */
  kind?: string;
  /** The day of its last check, or "Not checked". */
  lastCheck?: string;
  /** An open fault turns the last check red: the card's one warning. */
  fault?: boolean;
  plate?: string | null;
}

/** A truck. With no make or model its name is the title, once. */
export function TruckCard({
  platform,
  name,
  model,
  kind,
  lastCheck,
  fault = false,
  plate,
  ...props
}: TruckCardProps) {
  return (
    <RecordRow
      density="card"
      badge={model ? <RecordRowTag>{name}</RecordRowTag> : undefined}
      title={model || name}
      meta={kind}
      status={
        lastCheck ? (
          <CardPill
            platform={platform}
            label={lastCheck}
            tone={fault ? 'destructive' : 'neutral'}
            title={fault ? 'Open faults' : undefined}
          />
        ) : undefined
      }
      figure={plateOf(plate)}
      {...props}
    />
  );
}

export interface AuctionLotCardProps extends ResourceCardProps {
  /** "LOT 012". */
  lot: string;
  title: string;
  category: string;
  status: string;
  statusTone?: StatusTone;
  /** What it sold for once it has, what is asked until then. */
  price: string;
}

/** An auction lot in a list. Its card in the Grid view is LotCard. */
export function AuctionLotCard({
  platform,
  lot,
  title,
  category,
  status,
  statusTone,
  price,
  ...props
}: AuctionLotCardProps) {
  return (
    <RecordRow
      density="card"
      badge={<RecordRowTag>{lot}</RecordRowTag>}
      title={title}
      meta={category}
      status={<CardPill platform={platform} label={status} tone={statusTone} />}
      figure={price}
      {...props}
    />
  );
}
